/**
 * ohr-mixmatch-pricing.js
 * ------------------------------------------------------------------
 * "Buy 3, Get 1 Free" Mix & Match bundle — LIVE PRICE PANEL ONLY.
 *
 * This file's only job is cosmetic: read the customer's current slot
 * selections and render the live "now / was / you save / per manicure"
 * numbers, plus the dynamic FREE badge on whichever filled slot holds the
 * cheapest of the selected designs. It reads window.bundleModal (built by
 * ohr-bundle.js) but never mutates it.
 *
 * The actual "make the cheapest one free" pricing at checkout is done by a
 * native Shopify automatic Buy X Get Y discount (Discounts → Create
 * discount → Buy X get Y, with "Customer buys" and "Customer gets" both
 * pointing at the same collection) — see KURULUM.md for admin setup.
 *
 * HHP Customisation — why this file looks the way it does:
 * Two earlier approaches (a one-time `waitFor(window.bundleModal)` handoff,
 * then a delegated capture-phase click listener) both worked perfectly in
 * isolated live-browser testing, but proved unreliable on the real store:
 * confirmed live more than once that the exact same script content, when
 * it's the page's own <script defer> tag, ends up doing nothing (panel
 * stays hidden, stuck at its default values) — while re-fetching and
 * re-running that IDENTICAL code a few seconds later fixes it instantly.
 * That points to something about how/when this script's first run happens
 * on this store's preview environment (it has a live theme-editor preview
 * bar, which can re-render sections after the initial parse), not a logic
 * bug — but rather than keep chasing an exact root cause, this version is
 * built to not depend on getting any single "first run" right at all.
 *
 * There is no one-shot setup and no closured/cached references to the
 * panel, the buttons or window.bundleModal. Instead a single interval,
 * started immediately and unconditionally, re-reads everything from the
 * live DOM from scratch on every tick and re-renders the panel to match.
 * If the panel or window.bundleModal don't exist yet on a given tick, it
 * just no-ops and tries again next tick — so it self-heals regardless of
 * *when* things become ready, how many times a section re-renders, or
 * whether any particular click's event listener happened to fire. A
 * capture-phase click listener is also kept purely as a fast path (so
 * selecting/deselecting a design feels instant); it is not load-bearing.
 *
 * HHP Customisation — multiple panels, one source of truth:
 * There are now TWO ".bundle-price-summary" elements on the page (one
 * right under the product title, one under the design picker — see
 * product.liquid). recompute() queries ALL of them with
 * querySelectorAll and updates each identically from the same
 * window.bundleModal selection data, so they can never drift out of
 * sync with each other. Adding a third one later (or removing one)
 * needs no JS change — it just works as long as it carries the same
 * class and the same data-bundle-* child elements.
 */
(function () {
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
      try {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      } catch (e) {
        /* fall through to default formatting */
      }
    }
    return '£' + amount;
  }

  function clearBadges() {
    document.querySelectorAll('.bundle-slot-badge--dynamic').forEach(function (b) {
      b.classList.add('hidden');
    });
  }

  function badgeForSlot(slotNumber) {
    var slot = document.querySelector('.bundle-product-slot[data-slot="' + slotNumber + '"]');
    return slot ? slot.querySelector('.bundle-slot-badge--dynamic') : null;
  }

  // Re-reads every panel + window.bundleModal fresh every single call —
  // no stale/closured references anywhere in this function.
  function recompute() {
    var panels = document.querySelectorAll('.bundle-price-summary');
    var bm = window.bundleModal;
    if (!panels.length || !bm) return; // not ready yet on this tick — next tick will retry

    var selections = bm.slotSelections || {};
    var entries = Object.keys(selections).map(function (slotNum) {
      var productId = selections[slotNum];
      var card = document.querySelector('.bundle-product-card[data-product-id="' + productId + '"]');
      var price = card ? parseInt(card.getAttribute('data-price'), 10) : 0;
      return { slot: parseInt(slotNum, 10), price: isNaN(price) ? 0 : price };
    });

    clearBadges();

    if (!entries.length) {
      panels.forEach(function (panel) {
        panel.setAttribute('hidden', 'hidden');
      });
      // HHP Customisation: no selections yet — show the "From £X"
      // placeholder under the title again (e.g. the customer cleared
      // every slot after having picked some).
      document.querySelectorAll('[data-bundle-price-placeholder]').forEach(function (el) {
        el.removeAttribute('hidden');
      });
      return;
    }

    // HHP Customisation: at least one real selection exists now — the
    // working panel(s) below take over, so the placeholder gets out of
    // the way.
    document.querySelectorAll('[data-bundle-price-placeholder]').forEach(function (el) {
      el.setAttribute('hidden', 'hidden');
    });

    var total = entries.reduce(function (sum, e) {
      return sum + e.price;
    }, 0);
    var cheapest = entries.reduce(function (min, e) {
      return e.price < min.price ? e : min;
    }, entries[0]);

    panels.forEach(function (panel) {
      panel.removeAttribute('hidden');

      var forcedMax = parseInt(panel.getAttribute('data-max-items'), 10) || bm.MAX_SELECTIONS || 4;
      var manicuresPerBox = parseFloat(panel.getAttribute('data-manicures-per-box')) || 2;

      var isComplete = entries.length >= forcedMax;
      var payable = isComplete ? total - cheapest.price : total;
      var savings = isComplete ? cheapest.price : 0;

      if (isComplete) {
        var badge = badgeForSlot(cheapest.slot);
        if (badge) badge.classList.remove('hidden');
      }

      var totalEl = panel.querySelector('[data-bundle-total]');
      var compareEl = panel.querySelector('[data-bundle-compare]');
      var saveEl = panel.querySelector('[data-bundle-save]');
      var perManicureEl = panel.querySelector('[data-bundle-per-manicure]');

      if (totalEl) totalEl.textContent = formatMoney(payable);

      if (compareEl) {
        if (savings > 0) {
          compareEl.textContent = formatMoney(total);
          compareEl.classList.remove('hidden');
        } else {
          compareEl.classList.add('hidden');
        }
      }

      if (saveEl) {
        if (savings > 0) {
          // HHP Customisation: the top panel (under the title) shows a
          // "Save X%" pill badge instead of the lower panel's "You save
          // £X" text — see ohr-mixmatch-pricing.css for the badge
          // styling, scoped to .bundle-price-summary--top so the lower
          // panel is untouched.
          if (panel.classList.contains('bundle-price-summary--top')) {
            var savePercent = total > 0 ? Math.round((savings / total) * 100) : 0;
            saveEl.textContent = 'Save ' + savePercent + '%';
          } else {
            saveEl.textContent = 'You save ' + formatMoney(savings);
          }
          saveEl.classList.remove('hidden');
        } else {
          saveEl.classList.add('hidden');
        }
      }

      if (perManicureEl) {
        var manicures = entries.length * manicuresPerBox;
        var perManicure = manicures > 0 ? payable / manicures : 0;
        perManicureEl.textContent = manicures > 0 ? 'Only ' + formatMoney(perManicure) + ' per manicure' : '';
      }
    });
  }

  // Fast path: recompute right away on the relevant clicks, deferred by a
  // microtask so BundleModal's own click handling (which runs first and
  // updates slotSelections) has already finished. Purely a nice-to-have —
  // the interval below is what guarantees correctness.
  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (
        t.closest('.bundle-product-toggle') ||
        t.closest('.bundle-slot-remove') ||
        t.closest('[data-bundle-close]') ||
        t.closest('[data-add-to-bundle]')
      ) {
        Promise.resolve().then(recompute);
      }
    },
    true // capture — ohr-bundle.js's own handlers call e.stopPropagation()
  );

  // The actual guarantee: unconditional, immediate, self-healing poll.
  // No readyState check, no waitFor, no one-time setup to get right.
  recompute();
  setInterval(recompute, 250);
})();